/**
 * Chat Messaging Columns for TanStackTable
 *
 * Provides column definitions for Chat Messaging (Slack, Teams, GChat, Zulip) notifications.
 *
 * Columns:
 * 1. Integration - Chat icon + Provider name + Display name + Copy icon
 * 2. Severity Threshold - Severity badge with tooltip
 * 3. Actions - Ellipsis menu with Edit and Delete
 */
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  ChatBubbleLeftRightIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import React from 'react';

import { CellAlignment, Column } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { AllIntegrations } from '@/hooks/useIntegration';
import { NOTIFICATION_TABLE_COLUMN_WIDTHS } from '@/sections/settings/components/cards/notifications/constants';
import { SeverityThresholdCell } from '@/sections/settings/components/cells/SeverityThresholdCell';
import { CopyIconButton } from '@/sections/settings/components/primitives/CopyIconButton';
import { RowActionsMenu } from '@/sections/settings/components/primitives/RowActionsMenu';
import { Account, Integration } from '@/types';

interface ChatMessagingColumnsConfig {
  onDelete?: (integration: Account) => void;
  /** Handler for viewing chat configuration (severity-only editing) */
  onView: (integration: Account) => void;
}

/**
 * Column definitions for Chat Messaging TanStackTable.
 *
 * Provides:
 * - Integration column with chat icon, provider name, display name, and copy icon
 * - Severity threshold column with badge
 * - Actions column with Delete menu item (no Edit - webhook-based)
 */
export function getChatMessagingColumns({
  onDelete,
  onView,
}: ChatMessagingColumnsConfig): Column<Account>[] {
  return [
    {
      id: 'integration' as keyof Account,
      label: 'Integration',
      fixedWidth: NOTIFICATION_TABLE_COLUMN_WIDTHS.primaryValue,
      cell: item => {
        const integrationMeta = AllIntegrations[item.member as Integration];
        const providerName = integrationMeta?.name || 'Chat';
        const displayName =
          item.settings?.displayName || `${providerName} configured`;

        // Get current theme for icon path
        const currentTheme = document.documentElement.classList.contains('dark')
          ? 'dark'
          : 'light';

        return (
          <div className="flex items-center gap-2">
            {integrationMeta?.logo ? (
              <img
                src={`/icons/${currentTheme}/${integrationMeta.logo}`}
                alt={integrationMeta.name}
                className="size-5 shrink-0"
              />
            ) : (
              <ChatBubbleLeftRightIcon className="text-chariot-text-tertiary size-5 shrink-0" />
            )}
            <span className="text-chariot-text-primary truncate text-sm">
              {providerName} - {displayName}
            </span>
            {item.value && (
              <CopyIconButton
                value={item.value}
                label="Webhook URL"
                data-testid={`copy-chat-${item.key}`}
              />
            )}
          </div>
        );
      },
    },
    {
      id: 'severities' as keyof Account,
      label: (
        <span className="flex items-center gap-1">
          Severity Threshold
          <Tooltip title="Minimum vulnerability severity level that will trigger chat notifications">
            <InformationCircleIcon className="text-chariot-text-secondary size-4 shrink-0 cursor-pointer" />
          </Tooltip>
        </span>
      ),
      fixedWidth: NOTIFICATION_TABLE_COLUMN_WIDTHS.severities,
      cell: item => {
        const threshold = item.settings?.severityThreshold || 'MHC';
        return (
          <SeverityThresholdCell
            threshold={threshold}
            data-testid={`severity-${item.key}`}
          />
        );
      },
    },
    {
      id: '' as keyof Account,
      label: '',
      fixedWidth: NOTIFICATION_TABLE_COLUMN_WIDTHS.actions,
      align: 'right' as CellAlignment,
      cell: item => (
        <div className="flex justify-end">
          <RowActionsMenu
            testIdPrefix="chat"
            itemKey={item.key}
            actions={[
              {
                label: 'Edit',
                icon: <PencilIcon className="size-4" />,
                onClick: () => onView(item),
              },
              ...(onDelete
                ? [
                    {
                      label: 'Delete',
                      icon: <TrashIcon className="size-4" />,
                      onClick: () => onDelete(item),
                      variant: 'danger' as const,
                    },
                  ]
                : []),
            ]}
          />
        </div>
      ),
    },
  ];
}
